import { useState } from "react";
import { motion } from "framer-motion";
import { X } from "lucide-react";

/* জিজ্ঞেস-রসিদ — নাম-ফোন নিয়ে দোকানের হয়ে খবর (পেমেন্ট নেই) */
export default function OrderCard({ items, onClose }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [place, setPlace] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [rosid, setRosid] = useState(null);

  const submit = async () => {
    if (busy) return;
    setError("");
    setBusy(true);
    try {
      const { shopOrder } = await import("./repApi");
      const d = await shopOrder({
        name,
        phone,
        place,
        note,
        items: items.map((p) => ({ id: p.id, title: p.title, price: p.price, qty: 1 })),
      });
      setRosid(d.code);
    } catch (e) {
      setError(e.message || "রসিদ তৈরি হলো না — আবার চেষ্টা করুন");
    }
    setBusy(false);
  };

  return (
    <motion.div
      className="fixed inset-0 z-[93] flex items-end justify-center bg-dusk/90 p-0 backdrop-blur-md sm:items-center sm:p-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      data-testid="order-modal"
    >
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 30 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md rounded-t-2xl border border-gold/25 bg-plaster p-6 sm:rounded-2xl sm:p-8"
      >
        <button
          data-testid="order-close"
          onClick={onClose}
          className="absolute right-4 top-4 rounded-full bg-dusk/70 p-2 text-sand/70 transition-colors hover:text-sand"
          aria-label="বন্ধ করুন"
        >
          <X size={16} />
        </button>

        {rosid ? (
          <div className="py-6 text-center" data-testid="order-rosid">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-gold/40 bg-gold/10">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#e8b54d" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6 9 17l-5-5" />
              </svg>
            </div>
            <h3 className="mt-4 font-display text-2xl text-sand">রসিদ হয়ে গেল</h3>
            <p className="mt-2 text-sm text-sand/70">
              রসিদ-নং <span className="font-semibold text-gold">{rosid}</span>
            </p>
            <p className="mt-2 text-xs leading-relaxed text-sand/55">
              দোকান থেকে ফোনে খবর যাবে — দাম-কথা শেষে পোশাক হাতে বুঝিয়ে পাঠানো হবে। পেমেন্ট এখন কিছুই নেই।
            </p>
            <button
              data-testid="order-done"
              onClick={onClose}
              className="mt-6 rounded-full bg-alpana px-6 py-2.5 text-sm font-medium text-dusk transition-transform duration-300 hover:scale-105"
            >
              ঠিক আছে
            </button>
          </div>
        ) : (
          <>
            <h3 className="font-display text-2xl text-sand">অর্ডারের জিজ্ঞেস</h3>
            <p className="mt-1 text-xs text-sand/55">চিত্রা নাম-ফোন রেখে দোকানের হয়ে খবর করবেন।</p>
            <div className="mt-3 space-y-1 border-b border-gold/15 pb-3">
              {items.map((p) => (
                <div key={p.id} className="flex justify-between text-xs text-sand/75" data-testid="order-item">
                  <span>{p.title}</span>
                  <span className="text-gold">{p.price}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 space-y-3">
              <input
                data-testid="order-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="আপনার নাম"
                className="w-full rounded-xl border border-gold/25 bg-dusk/40 px-4 py-2.5 text-sm text-sand placeholder:text-sand/35 focus:border-gold/60 focus:outline-none"
              />
              <input
                data-testid="order-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="ফোন নম্বর"
                inputMode="tel"
                className="w-full rounded-xl border border-gold/25 bg-dusk/40 px-4 py-2.5 text-sm text-sand placeholder:text-sand/35 focus:border-gold/60 focus:outline-none"
              />
              <input
                value={place}
                onChange={(e) => setPlace(e.target.value)}
                placeholder="গ্রাম/শহর (চাইলে)"
                className="w-full rounded-xl border border-gold/25 bg-dusk/40 px-4 py-2.5 text-sm text-sand placeholder:text-sand/35 focus:border-gold/60 focus:outline-none"
              />
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="কিছু বলার থাকলে — মাপ, রং…"
                rows={2}
                className="w-full resize-none rounded-xl border border-gold/25 bg-dusk/40 px-4 py-2.5 text-sm text-sand placeholder:text-sand/35 focus:border-gold/60 focus:outline-none"
              />
            </div>
            {error && (
              <p data-testid="order-error" className="mt-3 text-xs text-vermilion">
                {error}
              </p>
            )}
            <button
              data-testid="order-submit"
              onClick={submit}
              disabled={busy || !name.trim() || !phone.trim()}
              className="mt-5 w-full rounded-full bg-alpana py-3 text-sm font-semibold text-dusk transition-all duration-300 hover:bg-gold disabled:opacity-40"
            >
              {busy ? "পাঠাচ্ছি…" : "জিজ্ঞেস-রসিদ পাঠান"}
            </button>
          </>
        )}
      </motion.div>
    </motion.div>
  );
}
